// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Places the API login token into Swagger Authorize without labelling the
 * button Authorized before the value is stored.
 * Instruction used: debugging.instructions.md
 * Instruction used: scope-discipline.instructions.md
 */

function isAuthHeaderForm(text) {
  const value = String(text || '')
  const namesAuth = /Name:\s*Auth\b/.test(value) || /\bAuth\s*\(apiKey\)/.test(value)
  const header = /In:\s*header/i.test(value)
  const isApiKey = /x-api-key/.test(value) || /api_key\s*\(apiKey\)/.test(value)
  return namesAuth && header && !isApiKey
}

export function bearerAuthSchemeNames(swagger) {
  const definitions = swagger && (swagger.securityDefinitions || (swagger.components && swagger.components.securitySchemes))
  if (!definitions) return []
  return Object.keys(definitions).filter(function (name) {
    if (name === 'api_key') return false
    const scheme = definitions[name]
    if (!scheme || scheme.type !== 'apiKey' || scheme.in !== 'header') return false
    return scheme.name === 'Auth' || scheme.name === 'Authorization'
  })
}

export function preauthorizeApiLoginToken(uiHandler, swagger, token) {
  if (!uiHandler || typeof uiHandler.preauthorizeApiKey !== 'function') return []
  if (!token) return []
  const names = bearerAuthSchemeNames(swagger)
  names.forEach(function (name) {
    uiHandler.preauthorizeApiKey(name, token)
  })
  return names
}

/**
 * Hands the loaded Swagger handler to the automator without putting it in
 * React state. State would re-render the open API page and rebuild Swagger.
 * Instruction used: debugging.instructions.md
 * Instruction used: scope-discipline.instructions.md
 */
export function handSwaggerUiToAutomator(automatorRef, uiHandler) {
  const automator = automatorRef && automatorRef.current
  if (!automator || typeof automator.receiveSwaggerUi !== 'function') return false
  automator.receiveSwaggerUi(uiHandler)
  return true
}

/**
 * The Authorize button locks when any scheme is set, including api_key.
 * The token is present only when the Auth header field holds it.
 * Instruction used: debugging.instructions.md
 */
export function authHeaderValueIsPresent(root) {
  if (!root || !root.querySelectorAll) return false
  const forms = root.querySelectorAll('form')
  let present = false
  forms.forEach(function (form) {
    const text = form.textContent || ''
    if (!isAuthHeaderForm(text)) return
    const input = form.querySelector('input[type="text"]')
    if (input) {
      if (String(input.value || '').length > 0) present = true
      return
    }
    if (/\*{3,}/.test(text)) present = true
  })
  return present
}

export function fillOpenAuthHeader(root, token, findReact) {
  if (!root || !token || !root.querySelectorAll) return false
  const forms = root.querySelectorAll('form')
  let filled = false
  forms.forEach(function (form) {
    const text = form.textContent || ''
    if (!isAuthHeaderForm(text)) return
    if (/\*{3,}/.test(text) && !form.querySelector('input[type="text"]')) {
      filled = true
      return
    }
    const input = form.querySelector('input[type="text"]')
    if (!input) return
    const reactInput = findReact && findReact(input)
    let reactAccepted = false
    if (reactInput && reactInput.props && reactInput.props.onChange) {
      reactInput.props.onChange({ target: { value: token } })
      reactAccepted = true
    }
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')
    if (setter && setter.set) setter.set.call(input, token)
    else input.value = token
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
    const authorize = form.querySelector('button.auth.authorize, button.authorize')
    if (authorize && /authorize/i.test(authorize.textContent || '')) authorize.click()
    filled = reactAccepted || String(input.value || '').length > 0
  })
  return filled
}
